import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getArticles, toArticlesApiError } from '../api/articlesApi';
import {
  ARTICLE_SORT_FIELDS,
  type ArticlePage,
  type ArticleQuery,
  type ArticleSortField,
} from '../types';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZES = [10, 20, 50] as const;

const emptyPage = (size: number): ArticlePage => ({
  currentPage: 1,
  totalPages: 0,
  pageSize: size,
  totalElements: 0,
  data: [],
});

const isSortField = (value: string | null): value is ArticleSortField =>
  !!value && ARTICLE_SORT_FIELDS.includes(value as ArticleSortField);

const isPageSize = (value: number): value is typeof PAGE_SIZES[number] =>
  PAGE_SIZES.includes(value as typeof PAGE_SIZES[number]);

export const useArticles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState<ArticlePage>(() => emptyPage(DEFAULT_PAGE_SIZE));
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const requestId = useRef(0);

  const query = useMemo<ArticleQuery>(() => {
    const requestedPage = Number(searchParams.get('page'));
    const requestedSize = Number(searchParams.get('size'));
    const requestedSort = searchParams.get('sort');
    return {
      page: Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1,
      size: Number.isInteger(requestedSize) && isPageSize(requestedSize) ? requestedSize : DEFAULT_PAGE_SIZE,
      sort: isSortField(requestedSort) ? requestedSort : 'id',
    };
  }, [searchParams]);

  const loadArticles = useCallback(async () => {
    const activeRequest = ++requestId.current;
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await getArticles(query);
      if (activeRequest === requestId.current) {
        setPageData(response);
        setHasLoaded(true);
      }
    } catch (error: unknown) {
      if (activeRequest === requestId.current) {
        setLoadError(toArticlesApiError(error).message);
        setHasLoaded(true);
      }
    } finally {
      if (activeRequest === requestId.current) setIsLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void loadArticles();
    return () => {
      requestId.current += 1;
    };
  }, [loadArticles]);

  const updateQuery = useCallback((updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = useCallback((page: number) => {
    updateQuery({ page: page > 1 ? String(page) : undefined });
  }, [updateQuery]);

  const setPageSize = useCallback((size: number) => {
    updateQuery({ size: size === DEFAULT_PAGE_SIZE ? undefined : String(size), page: undefined });
  }, [updateQuery]);

  const setSort = useCallback((sort: ArticleSortField) => {
    updateQuery({ sort: sort === 'id' ? undefined : sort, page: undefined });
  }, [updateQuery]);

  return {
    query,
    pageData,
    isInitialLoading: !hasLoaded && isLoading,
    isTableLoading: isLoading,
    loadError,
    setPage,
    setPageSize,
    setSort,
    refresh: loadArticles,
    retry: loadArticles,
  };
};
